import { Select } from "@package/subframe/components/Select";
import { Tabs } from "@package/subframe/components/Tabs";
import { TextField } from "@package/subframe/components/TextField";
import {
  FeatherAlertTriangle,
  FeatherBookOpen,
  FeatherClock,
  FeatherHistory,
  FeatherSearch,
} from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { isPast } from "date-fns";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { ChecklistManagerTable } from "@/components/checklist-manager/checklist-manager-table";
import { ConfirmAcknowledgeChecklistDialog } from "@/components/checklists/ConfirmAcknowledgeChecklistDialog";
import { Pagination } from "@/components/ui/pagination";
import { Spinner } from "@/components/ui/spinner";
import { TableScrollContainer } from "@/components/ui/table-scroll-container";
import { useAuth } from "@/contexts/AuthContext";
import { useNursery } from "@/contexts/NurseryContext";
import { useOrganisation } from "@/contexts/OrganisationContext";
import { useListCategoriesQueryControllerList } from "@/generated/categories/categories";
import type { ScheduledQuestionnaireListItemDto } from "@/generated/model";
import { useAcknowledgeChecklistControllerAcknowledge } from "@/generated/scheduled-questionnaires/scheduled-questionnaires";
import { useChecklistManagerData } from "@/hooks/use-checklist-manager-data";
import {
  acknowledgeErrorMessage,
  type ChecklistManagerTab,
  type ChecklistSortColumn,
  PAGE_SIZE,
  type SortDir,
} from "@/lib/checklist-manager";
import { canAcknowledgeFailedChecks } from "@/lib/roles";
import { TemplatesPage } from "@/routes/_authenticated/templates/index";

const routeApi = getRouteApi("/_authenticated/checklist-manager/");

/** Default sort per tab when the user hasn't picked a column. */
const DEFAULT_SORT: Record<
  Exclude<ChecklistManagerTab, "templates">,
  { column: ChecklistSortColumn; dir: SortDir }
> = {
  history: { column: "completedAt", dir: "desc" },
  "due-soon": { column: "due", dir: "asc" },
  failed: { column: "completedAt", dir: "desc" },
};

function CountBadge({
  count,
  tone,
}: {
  count: number;
  tone: "error" | "warning";
}) {
  if (count <= 0) return null;
  const toneClass =
    tone === "error"
      ? "bg-error-50 text-error-600"
      : "bg-warning-50 text-warning-700";
  return (
    <span
      className={`ml-2 inline-flex items-center justify-center rounded-full px-2 text-caption-bold font-caption-bold ${toneClass}`}
    >
      {count}
    </span>
  );
}

export function ChecklistManagerPage() {
  const navigate = useNavigate();
  const search = routeApi.useSearch();
  const queryClient = useQueryClient();
  const { selectedNurseryId, selectedNursery, nurseryRole } = useNursery();
  const { isPlatformAdmin } = useAuth();
  const { isOrgAdmin } = useOrganisation();

  const canAcknowledge = canAcknowledgeFailedChecks({
    isPlatformAdmin,
    isOrgAdmin,
    nurseryRole,
  });

  const acknowledgeMutation = useAcknowledgeChecklistControllerAcknowledge();
  const [acknowledgeTarget, setAcknowledgeTarget] =
    useState<ScheduledQuestionnaireListItemDto | null>(null);

  const refreshAfterAcknowledge = () =>
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey?.[0];
        return (
          typeof key === "string" &&
          (key.includes("/scheduled-questionnaires") ||
            key.includes("/dashboard"))
        );
      },
      refetchType: "all",
    });

  const handleAcknowledge = async () => {
    if (!acknowledgeTarget) return;
    try {
      await acknowledgeMutation.mutateAsync({
        scheduledQuestionnaireId: acknowledgeTarget.id,
      });
      setAcknowledgeTarget(null);
      await refreshAfterAcknowledge();
      toast.success("Failed checks acknowledged");
    } catch (err) {
      const { message, alreadyAcknowledged } = acknowledgeErrorMessage(err);
      // Already cleared elsewhere — the row is stale, not the request wrong.
      if (alreadyAcknowledged) {
        setAcknowledgeTarget(null);
        await refreshAfterAcknowledge();
        toast.info(message);
        return;
      }
      toast.error(message);
    }
  };

  const tab = (search.tab as ChecklistManagerTab) ?? "history";
  const [categoryId, setCategoryId] = useState<string | undefined>(undefined);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [sortColumn, setSortColumn] = useState<ChecklistSortColumn | undefined>(
    undefined,
  );
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  // Debounce the search box so each keystroke doesn't hit the API.
  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(0);
    }, 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  const { data: categoriesResponse } = useListCategoriesQueryControllerList();
  const categories = categoriesResponse?.data.items ?? [];

  // Resolve effective sort: explicit column, else the tab's default.
  const effectiveSort =
    tab === "templates"
      ? undefined
      : sortColumn
        ? { column: sortColumn, dir: sortDir }
        : DEFAULT_SORT[tab];

  const { items, total, isLoading, dueSoonCount, failedChecksCount } =
    useChecklistManagerData({
      nurseryId: selectedNurseryId ?? undefined,
      tab,
      categoryId,
      search: debouncedSearch,
      sortBy: effectiveSort?.column,
      sortDir: effectiveSort?.dir ?? "desc",
      page,
      pageSize,
    });

  const setTab = (next: ChecklistManagerTab) => {
    setPage(0);
    setSortColumn(undefined);
    navigate({ to: "/checklist-manager", search: { tab: next } });
  };

  const handleSort = (column: ChecklistSortColumn) => {
    if (sortColumn === column) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDir("asc");
    }
    setPage(0);
  };

  return (
    <div className="flex h-full w-full flex-col items-start gap-6 bg-white animate-fade-in">
      {/* Header */}
      <div className="flex w-full flex-wrap items-center gap-4">
        <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
          <span className="text-metrics font-metrics text-primary-navy-800">
            Checklist manager
          </span>
          <span className="text-body font-body text-subtext-color">
            Create, manage and monitor all checklists in your setting.
          </span>
        </div>
      </div>

      {/* Tabs */}
      <Tabs>
        <Tabs.Item
          active={tab === "history"}
          icon={<FeatherHistory />}
          onClick={() => setTab("history")}
        >
          History
        </Tabs.Item>
        <Tabs.Item
          active={tab === "due-soon"}
          icon={<FeatherClock />}
          onClick={() => setTab("due-soon")}
        >
          Due soon
          <CountBadge count={dueSoonCount} tone="warning" />
        </Tabs.Item>
        <Tabs.Item
          active={tab === "failed"}
          icon={<FeatherAlertTriangle />}
          onClick={() => setTab("failed")}
        >
          Failed checks
          <CountBadge count={failedChecksCount} tone="error" />
        </Tabs.Item>
        <Tabs.Item
          active={tab === "templates"}
          icon={<FeatherBookOpen />}
          onClick={() => setTab("templates")}
        >
          Templates
        </Tabs.Item>
      </Tabs>

      {tab === "templates" ? (
        <TemplatesPage />
      ) : (
        <div className="flex w-full flex-col items-start gap-4 pb-8">
          {/* Filters: category + search (no Filters button — tabs replace it) */}
          <div className="flex w-full flex-wrap items-center gap-3">
            <Select
              placeholder="All categories"
              value={categoryId ?? "ALL"}
              onValueChange={(val) => {
                setCategoryId(val === "ALL" ? undefined : val);
                setPage(0);
              }}
            >
              <Select.Item value="ALL">All categories</Select.Item>
              {categories.map((category) => (
                <Select.Item key={category.id} value={category.id}>
                  {category.name}
                </Select.Item>
              ))}
            </Select>
            <TextField
              className="h-auto w-full lg:w-72"
              variant="outline"
              size="base"
              icon={<FeatherSearch />}
            >
              <TextField.Input
                placeholder="Search checklists"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </TextField>
          </div>

          {/* Table */}
          {isLoading ? (
            <div className="flex w-full items-center justify-center py-12">
              <Spinner className="h-8 w-8 text-primary" />
            </div>
          ) : items.length === 0 ? (
            <div className="flex w-full items-center justify-center py-16">
              <span className="text-body font-body text-subtext-color">
                No checklists to show for this view.
              </span>
            </div>
          ) : (
            <>
              <TableScrollContainer>
                <ChecklistManagerTable
                  items={items}
                  tab={tab}
                  nurseryName={selectedNursery?.name ?? "—"}
                  activeColumn={effectiveSort?.column}
                  activeDir={effectiveSort?.dir ?? "desc"}
                  onSort={handleSort}
                  onView={(id) => {
                    const row = items.find((item) => item.id === id);
                    const isFuture = row
                      ? !isPast(new Date(row.availableFrom))
                      : false;
                    navigate({
                      to: "/checklists/$id",
                      params: { id },
                      search: isFuture ? { mode: "preview" } : undefined,
                    });
                  }}
                  canAcknowledge={canAcknowledge}
                  onAcknowledge={setAcknowledgeTarget}
                />
              </TableScrollContainer>

              <Pagination
                page={page + 1}
                pageSize={pageSize}
                totalCount={total}
                onPageChange={(next) => setPage(next - 1)}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setPage(0);
                }}
                itemNoun="checklists"
              />
            </>
          )}
        </div>
      )}

      <ConfirmAcknowledgeChecklistDialog
        open={acknowledgeTarget !== null}
        onOpenChange={(open) => {
          if (!open) setAcknowledgeTarget(null);
        }}
        checklistName={acknowledgeTarget?.template?.name ?? "This checklist"}
        failedCheckCount={acknowledgeTarget?.failedCheckCount ?? 0}
        isSaving={acknowledgeMutation.isPending}
        onConfirm={handleAcknowledge}
      />
    </div>
  );
}

export default ChecklistManagerPage;
